import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Package, Loader2, Eye, EyeOff, ChevronDown, ChevronRight, Truck, MapPin, Search, X, Link2, Layers } from "lucide-react";
import { logisticsApi, equivalencesApi, api } from "@/lib/api";
import { toast } from "sonner";

const STATUS_OPTIONS = [
  { value: "todo", label: "À faire", color: "bg-orange-100 text-orange-800" },
  { value: "requested", label: "Demandé", color: "bg-slate-200 text-slate-800" },
  { value: "accepted", label: "Accepté", color: "bg-blue-100 text-blue-800" },
  { value: "in_delivery", label: "En livraison", color: "bg-amber-100 text-amber-800" },
  { value: "delivered", label: "Livré / Validé", color: "bg-emerald-100 text-emerald-800" },
];

const DELIVERY_PLACE_OPTIONS = [
  { value: "warehouse", label: "À l'entrepôt" },
  { value: "office", label: "Au bureau" },
  { value: "site", label: "Sur le chantier" },
  { value: "supplier", label: "Chez le fournisseur" },
  { value: "other", label: "Autre (saisir adresse)" },
];

const DELIVERY_PLACE_LABEL = Object.fromEntries(DELIVERY_PLACE_OPTIONS.map((o) => [o.value, o.label]));

const STATUS_LABEL = Object.fromEntries(STATUS_OPTIONS.map((s) => [s.value, s.label]));
const STATUS_COLOR = Object.fromEntries(STATUS_OPTIONS.map((s) => [s.value, s.color]));

/**
 * ArticlePicker — combobox de recherche permettant de :
 * - Sélectionner un article du catalogue (filtré par catégorie)
 * - Sélectionner un produit fournisseur (avec liaison product_id)
 * - Sélectionner un groupe d'équivalence (avec liaison equivalence_group_id)
 * - Saisir librement un nouveau libellé → création à la volée d'un catalogue
 *
 * onSelect reçoit : { kind, item, label }
 *   kind ∈ { 'catalog', 'product', 'group', 'custom' }
 */
function ArticlePicker({ catId, catalog, products, groups, value, onSelect, disabled, testId }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const lower = query.trim().toLowerCase();
  const catalogFiltered = useMemo(() => {
    const list = catalog.filter((c) => c.category_id === catId);
    if (!lower) return list.slice(0, 20);
    return list.filter((c) => (c.name || "").toLowerCase().includes(lower)).slice(0, 20);
  }, [catalog, catId, lower]);
  const productsFiltered = useMemo(() => {
    if (!lower) return products.slice(0, 10);
    return products
      .filter((p) => (p.name || "").toLowerCase().includes(lower) || (p.reference || "").toLowerCase().includes(lower))
      .slice(0, 15);
  }, [products, lower]);
  const groupsFiltered = useMemo(() => {
    if (!lower) return groups.slice(0, 10);
    return groups.filter((g) => (g.name || "").toLowerCase().includes(lower)).slice(0, 15);
  }, [groups, lower]);

  const showCreate = lower.length >= 2 && catalogFiltered.every((c) => c.name.toLowerCase() !== lower);

  const handlePick = (kind, item, label) => {
    onSelect({ kind, item, label });
    setQuery(label);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); if (value) onSelect(null); }}
          onFocus={() => setOpen(true)}
          placeholder={catId ? "Rechercher ou saisir libellé…" : "Choisis d'abord la catégorie"}
          disabled={disabled}
          className="pl-7 pr-7"
          data-testid={testId}
        />
        {query && (
          <button
            type="button"
            onClick={() => { setQuery(""); onSelect(null); setOpen(false); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {open && catId && (
        <div
          className="absolute z-20 mt-1 w-full bg-white border rounded-md shadow-lg max-h-[320px] overflow-auto"
          data-testid={`${testId}-popover`}
        >
          {/* Catalog */}
          {catalogFiltered.length > 0 && (
            <div>
              <div className="px-2 py-1 text-[10px] uppercase font-semibold text-muted-foreground bg-slate-50 sticky top-0">Catalogue</div>
              {catalogFiltered.map((c) => (
                <button
                  key={`cat-${c.id}`}
                  type="button"
                  onClick={() => handlePick("catalog", c, c.name)}
                  className="w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50 flex items-center gap-2"
                  data-testid={`picker-catalog-${c.id}`}
                >
                  <Package className="h-3.5 w-3.5 text-blue-600 shrink-0" />
                  <span className="truncate">{c.name}</span>
                  {c.product_id && <Badge variant="secondary" className="text-[9px] ml-auto"><Link2 className="h-2.5 w-2.5 mr-0.5" />Produit</Badge>}
                  {c.equivalence_group_id && <Badge variant="secondary" className="text-[9px] ml-auto"><Layers className="h-2.5 w-2.5 mr-0.5" />Groupe</Badge>}
                </button>
              ))}
            </div>
          )}
          {/* Products */}
          {productsFiltered.length > 0 && (
            <div>
              <div className="px-2 py-1 text-[10px] uppercase font-semibold text-muted-foreground bg-blue-50 sticky top-0">Produits fournisseurs</div>
              {productsFiltered.map((p) => (
                <button
                  key={`prod-${p.id}`}
                  type="button"
                  onClick={() => handlePick("product", p, `${p.name}${p.supplier_name ? ` · ${p.supplier_name}` : ""}`)}
                  className="w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50 flex items-center gap-2"
                  data-testid={`picker-product-${p.id}`}
                >
                  <Link2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="truncate">{p.name}</div>
                    <div className="text-[10px] text-muted-foreground truncate">{p.supplier_name || "—"} · {p.reference_unit || ""}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
          {/* Groups */}
          {groupsFiltered.length > 0 && (
            <div>
              <div className="px-2 py-1 text-[10px] uppercase font-semibold text-muted-foreground bg-amber-50 sticky top-0">Groupes d&apos;équivalence</div>
              {groupsFiltered.map((g) => (
                <button
                  key={`grp-${g.id}`}
                  type="button"
                  onClick={() => handlePick("group", g, `[Groupe] ${g.name}`)}
                  className="w-full text-left px-3 py-1.5 text-sm hover:bg-amber-50 flex items-center gap-2"
                  data-testid={`picker-group-${g.id}`}
                >
                  <Layers className="h-3.5 w-3.5 text-amber-600 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="truncate">{g.name}</div>
                    <div className="text-[10px] text-muted-foreground">{g.reference_unit || "—"} · {(g.product_ids || []).length} produit(s)</div>
                  </div>
                </button>
              ))}
            </div>
          )}
          {/* Create new */}
          {showCreate && (
            <button
              type="button"
              onClick={() => handlePick("custom", null, query.trim())}
              className="w-full text-left px-3 py-2 text-sm border-t bg-emerald-50/60 hover:bg-emerald-100 flex items-center gap-2"
              data-testid="picker-create-new"
            >
              <Plus className="h-3.5 w-3.5 text-emerald-700" />
              Créer « <strong>{query.trim()}</strong> » dans le catalogue
            </button>
          )}
          {catalogFiltered.length === 0 && productsFiltered.length === 0 && groupsFiltered.length === 0 && !showCreate && (
            <div className="px-3 py-3 text-xs text-muted-foreground italic">Tape pour rechercher ou saisir un libellé…</div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Panneau "Détails" déroulant sous une ligne d'élément logistique :
 * dates de livraison à respecter / annoncée, lieu de livraison, toggle
 * "afficher dates et lieu sur la fiche", et option "ouvrier doit récupérer".
 */
function LogisticsItemDetails({ item, orderId, onSaved }) {
  const [state, setState] = useState({
    delivery_due_date: item.delivery_due_date || "",
    delivery_announced_date: item.delivery_announced_date || "",
    delivery_place: item.delivery_place || "",
    delivery_place_custom: item.delivery_place_custom || "",
    show_delivery_on_worksheet: !!item.show_delivery_on_worksheet,
    worker_pickup_required: !!item.worker_pickup_required,
    pickup_address: item.pickup_address || "",
    pickup_hours: item.pickup_hours || "",
    comments: item.comments || "",
  });
  const [saving, setSaving] = useState(false);

  const update = (patch) => setState((s) => ({ ...s, ...patch }));

  const save = async () => {
    try {
      setSaving(true);
      const payload = { ...state };
      // Normalize empty strings
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") payload[k] = null;
      });
      await logisticsApi.updateOrderLogisticsItem(orderId, item.id, payload);
      toast.success("Détails enregistrés");
      await onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de mise à jour");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="bg-slate-50 border-t px-4 py-3 space-y-3 text-sm"
      data-testid={`logi-details-${item.id}`}
    >
      <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
        <div className="md:col-span-3">
          <Label className="text-xs">Date à respecter</Label>
          <Input
            type="date"
            value={state.delivery_due_date || ""}
            onChange={(e) => update({ delivery_due_date: e.target.value })}
            data-testid={`logi-due-date-${item.id}`}
          />
        </div>
        <div className="md:col-span-3">
          <Label className="text-xs">Date annoncée fournisseur</Label>
          <Input
            type="date"
            value={state.delivery_announced_date || ""}
            onChange={(e) => update({ delivery_announced_date: e.target.value })}
            data-testid={`logi-announced-date-${item.id}`}
          />
        </div>
        <div className="md:col-span-3">
          <Label className="text-xs">Lieu de livraison</Label>
          <Select
            value={state.delivery_place || ""}
            onValueChange={(v) => update({ delivery_place: v })}
          >
            <SelectTrigger data-testid={`logi-place-${item.id}`}>
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent>
              {DELIVERY_PLACE_OPTIONS.map((o) => (
                <SelectItem
                  key={o.value}
                  value={o.value}
                  data-testid={`logi-place-opt-${o.value}-${item.id}`}
                >
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-3 flex items-end">
          <label className="flex items-center gap-2 text-xs">
            <Switch
              checked={state.show_delivery_on_worksheet}
              onCheckedChange={(v) => update({ show_delivery_on_worksheet: v })}
              data-testid={`logi-show-delivery-${item.id}`}
            />
            Afficher date + lieu sur la fiche
          </label>
        </div>
        {state.delivery_place === "other" && (
          <div className="md:col-span-12">
            <Label className="text-xs">Adresse complète de livraison</Label>
            <Input
              value={state.delivery_place_custom}
              onChange={(e) => update({ delivery_place_custom: e.target.value })}
              placeholder="Ex: Bricoman, 12 rue des lys à Avignon"
              data-testid={`logi-place-custom-${item.id}`}
            />
          </div>
        )}
      </div>

      <div className="border-t pt-3 space-y-2">
        <label className="flex items-center gap-2 text-xs font-medium">
          <Switch
            checked={state.worker_pickup_required}
            onCheckedChange={(v) => update({ worker_pickup_required: v })}
            data-testid={`logi-pickup-toggle-${item.id}`}
          />
          L&apos;ouvrier doit récupérer cet élément
        </label>
        {state.worker_pickup_required && (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
            <div className="md:col-span-7">
              <Label className="text-xs">Adresse complète</Label>
              <Input
                value={state.pickup_address}
                onChange={(e) => update({ pickup_address: e.target.value })}
                placeholder="Ex: Bricoman, 12 rue des lys, 84000 Avignon"
                data-testid={`logi-pickup-address-${item.id}`}
              />
            </div>
            <div className="md:col-span-5">
              <Label className="text-xs">Horaires</Label>
              <Input
                value={state.pickup_hours}
                onChange={(e) => update({ pickup_hours: e.target.value })}
                placeholder="Ex: 7h30 - 17h"
                data-testid={`logi-pickup-hours-${item.id}`}
              />
            </div>
          </div>
        )}
      </div>

      <div className="border-t pt-3">
        <Label className="text-xs">Commentaire (optionnel)</Label>
        <Textarea
          rows={2}
          value={state.comments || ""}
          onChange={(e) => update({ comments: e.target.value })}
          placeholder="Précisions internes…"
          data-testid={`logi-comments-${item.id}`}
        />
      </div>

      <div className="flex justify-end">
        <Button
          type="button"
          size="sm"
          onClick={save}
          disabled={saving}
          data-testid={`logi-details-save-${item.id}`}
        >
          {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
          Enregistrer les détails
        </Button>
      </div>
    </div>
  );
}

/**
 * Section logistique d'une commande : formulaire d'ajout (catégorie → article + "Autre" → quantité)
 * + liste des éléments avec statut cliquable, suppression et toggle "Afficher dans fiche".
 */
export default function OrderLogisticsSection({ order, onOrderRefresh }) {
  const [categories, setCategories] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [products, setProducts] = useState([]);
  const [equivGroups, setEquivGroups] = useState([]);
  const [items, setItems] = useState(order?.logistics_items || []);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  // Form state
  const [catId, setCatId] = useState("");
  const [selection, setSelection] = useState(null); // { kind, item, label } from ArticlePicker
  const [linkProductId, setLinkProductId] = useState(""); // optional manual link on create
  const [linkGroupId, setLinkGroupId] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [adding, setAdding] = useState(false);

  useEffect(() => { setItems(order?.logistics_items || []); }, [order?.logistics_items, order?.id]);

  const fetchRefs = useCallback(async () => {
    try {
      setLoading(true);
      const [cats, cat, prods, grps] = await Promise.all([
        logisticsApi.getLogisticsCategories(),
        logisticsApi.getLogisticsCatalog(),
        api.getAllProducts().catch(() => ({ data: [] })),
        equivalencesApi.listGroups().catch(() => ({ data: [] })),
      ]);
      setCategories(cats.data || []);
      setCatalog(cat.data || []);
      setProducts(prods.data || []);
      setEquivGroups(grps.data || []);
    } catch (e) {
      toast.error("Erreur de chargement de la logistique");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRefs(); }, [fetchRefs]);

  const catById = useMemo(
    () => Object.fromEntries(categories.map((c) => [c.id, c])),
    [categories],
  );
  const catalogById = useMemo(
    () => Object.fromEntries(catalog.map((c) => [c.id, c])),
    [catalog],
  );
  const productById = useMemo(
    () => Object.fromEntries(products.map((p) => [p.id, p])),
    [products],
  );
  const groupById = useMemo(
    () => Object.fromEntries(equivGroups.map((g) => [g.id, g])),
    [equivGroups],
  );

  const reload = async () => {
    if (!order?.id) return;
    // ask parent to refresh the order so items list comes back
    await onOrderRefresh?.();
  };

  const handleAdd = async (e) => {
    e?.preventDefault?.();
    if (!order?.id) return;
    if (!catId) {
      toast.error("Choisis une catégorie");
      return;
    }
    if (!selection) {
      toast.error("Choisis un article ou saisis un libellé");
      return;
    }
    try {
      setAdding(true);
      const payload = {
        category_id: catId,
        quantity: Math.max(1, parseInt(quantity, 10) || 1),
      };
      if (selection.kind === "catalog") {
        payload.catalog_item_id = selection.item.id;
        payload.product_id = selection.item.product_id || null;
        payload.equivalence_group_id = selection.item.equivalence_group_id || null;
      } else if (selection.kind === "product") {
        // Auto-create catalog entry tied to this product
        payload.catalog_item_name = selection.item.name;
        payload.product_id = selection.item.id;
      } else if (selection.kind === "group") {
        payload.catalog_item_name = `[Groupe] ${selection.item.name}`;
        payload.equivalence_group_id = selection.item.id;
      } else if (selection.kind === "custom") {
        // Free text → create catalog entry on the fly with optional link
        payload.catalog_item_name = selection.label;
        if (linkProductId) payload.product_id = linkProductId;
        if (linkGroupId) payload.equivalence_group_id = linkGroupId;
      }
      await logisticsApi.addOrderLogisticsItem(order.id, payload);
      toast.success("Élément ajouté");
      setSelection(null); setLinkProductId(""); setLinkGroupId(""); setQuantity(1);
      await reload();
      await fetchRefs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'ajout");
    } finally {
      setAdding(false);
    }
  };

  const handleStatusChange = async (item, newStatus) => {
    try {
      await logisticsApi.updateOrderLogisticsItem(order.id, item.id, { status: newStatus });
      await reload();
    } catch (e) {
      toast.error("Erreur de mise à jour");
    }
  };

  const handleToggleShow = async (item) => {
    try {
      await logisticsApi.updateOrderLogisticsItem(order.id, item.id, {
        show_in_worksheet: !item.show_in_worksheet,
      });
      await reload();
    } catch (e) {
      toast.error("Erreur de mise à jour");
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm("Supprimer cet élément ?")) return;
    try {
      await logisticsApi.deleteOrderLogisticsItem(order.id, item.id);
      toast.success("Élément supprimé");
      await reload();
    } catch (e) {
      toast.error("Erreur de suppression");
    }
  };

  // Group items by category for nice display
  const grouped = useMemo(() => {
    const byCat = {};
    for (const it of items) {
      const c = catById[it.category_id];
      const key = it.category_id || "none";
      byCat[key] = byCat[key] || { category: c, items: [] };
      byCat[key].items.push(it);
    }
    return Object.values(byCat).sort((a, b) =>
      (a.category?.name || "").localeCompare(b.category?.name || ""),
    );
  }, [items, catById]);

  const renderLabel = (it) => {
    if (it.catalog_item_id) {
      const c = catalogById[it.catalog_item_id];
      return c?.name || it.custom_label || "—";
    }
    return it.custom_label || "—";
  };

  return (
    <Card data-testid="order-logistics-section">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Package className="h-4 w-4 text-blue-700" />
          Logistique & besoins
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Add form */}
        <form
          onSubmit={handleAdd}
          className="grid grid-cols-1 md:grid-cols-12 gap-2 p-3 border rounded-md bg-slate-50"
        >
          <div className="md:col-span-3">
            <Label className="text-xs">Catégorie</Label>
            <Select
              value={catId}
              onValueChange={(v) => { setCatId(v); setSelection(null); setLinkProductId(""); setLinkGroupId(""); }}
              disabled={loading}
            >
              <SelectTrigger data-testid="logi-cat-select">
                <SelectValue placeholder="Catégorie..." />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id} data-testid={`logi-cat-${c.id}`}>
                    {c.name}{c.est_interne_administratif ? " · admin" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-7">
            <Label className="text-xs">Article (rechercher ou créer)</Label>
            <ArticlePicker
              catId={catId}
              catalog={catalog}
              products={products}
              groups={equivGroups}
              value={selection}
              onSelect={setSelection}
              disabled={!catId}
              testId="logi-article-picker"
            />
            {selection && (
              <div className="mt-1 text-xs flex items-center gap-2 flex-wrap" data-testid="logi-selection-summary">
                {selection.kind === "catalog" && <Badge variant="secondary"><Package className="h-3 w-3 mr-1" />Catalogue : {selection.label}</Badge>}
                {selection.kind === "product" && <Badge variant="secondary" className="bg-emerald-50 text-emerald-800"><Link2 className="h-3 w-3 mr-1" />Produit : {selection.label}</Badge>}
                {selection.kind === "group" && <Badge variant="secondary" className="bg-amber-50 text-amber-800"><Layers className="h-3 w-3 mr-1" />Groupe : {selection.item.name}</Badge>}
                {selection.kind === "custom" && <Badge variant="secondary" className="bg-blue-50 text-blue-800"><Plus className="h-3 w-3 mr-1" />Nouveau : {selection.label}</Badge>}
              </div>
            )}
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">Quantité</Label>
            <Input
              type="number"
              min={1}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              data-testid="logi-quantity-input"
            />
          </div>
          {selection?.kind === "custom" && (
            <div className="md:col-span-12 grid grid-cols-1 md:grid-cols-2 gap-2 border-t pt-2 mt-1">
              <div>
                <Label className="text-xs">Lier à un produit fournisseur (optionnel)</Label>
                <Select value={linkProductId || "__none__"} onValueChange={(v) => { setLinkProductId(v === "__none__" ? "" : v); if (v !== "__none__") setLinkGroupId(""); }}>
                  <SelectTrigger data-testid="logi-link-product-select">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Aucun —</SelectItem>
                    {products.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name} · {p.supplier_name || "—"}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Ou lier à un groupe d&apos;équivalence (optionnel)</Label>
                <Select value={linkGroupId || "__none__"} onValueChange={(v) => { setLinkGroupId(v === "__none__" ? "" : v); if (v !== "__none__") setLinkProductId(""); }}>
                  <SelectTrigger data-testid="logi-link-group-select">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Aucun —</SelectItem>
                    {equivGroups.map((g) => (
                      <SelectItem key={g.id} value={g.id}>[Groupe] {g.name} · {g.reference_unit || ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <div className="md:col-span-12 flex justify-end">
            <Button
              type="submit"
              disabled={adding || !catId || !selection}
              data-testid="logi-add-btn"
            >
              {adding ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
              Ajouter
            </Button>
          </div>
        </form>

        {/* Items grouped */}
        {grouped.length === 0 ? (
          <p className="text-sm text-muted-foreground italic" data-testid="logi-empty">
            Aucun besoin logistique pour le moment.
          </p>
        ) : (
          <div className="space-y-3">
            {grouped.map((g) => (
              <div
                key={g.category?.id || "none"}
                className="border rounded-md overflow-hidden"
                data-testid={`logi-cat-group-${g.category?.id || "none"}`}
              >
                <div
                  className="flex items-center gap-2 px-3 py-2 border-b text-sm font-medium"
                  style={{ backgroundColor: g.category?.color ? `${g.category.color}15` : "#f1f5f9" }}
                >
                  <span
                    className="inline-block w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: g.category?.color || "#94a3b8" }}
                  />
                  {g.category?.name || "Sans catégorie"}
                  {g.category?.est_interne_administratif && (
                    <Badge variant="secondary" className="text-xs">Admin interne</Badge>
                  )}
                  <span className="ml-auto text-xs text-muted-foreground">
                    {g.items.filter((i) => i.status === "delivered").length}/{g.items.length} livrés
                  </span>
                </div>
                <ul className="divide-y">
                  {g.items.map((it) => (
                    <li
                      key={it.id}
                      className="text-sm"
                      data-testid={`logi-item-row-${it.id}`}
                    >
                      <div className="flex flex-col md:flex-row md:items-center gap-2 px-3 py-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 px-1"
                          onClick={() => setExpandedId(expandedId === it.id ? null : it.id)}
                          data-testid={`logi-expand-${it.id}`}
                          title="Détails (dates, lieu, ramassage)"
                        >
                          {expandedId === it.id
                            ? <ChevronDown className="h-4 w-4" />
                            : <ChevronRight className="h-4 w-4" />}
                        </Button>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate flex items-center gap-1.5 flex-wrap">
                            {renderLabel(it)}
                            {(it.product_id || catalogById[it.catalog_item_id]?.product_id) && (() => {
                              const pid = it.product_id || catalogById[it.catalog_item_id]?.product_id;
                              const p = productById[pid];
                              return p ? (
                                <Badge variant="secondary" className="text-[10px] bg-emerald-50 text-emerald-800" title={`Produit ${p.supplier_name || ""}`}>
                                  <Link2 className="h-2.5 w-2.5 mr-0.5" />
                                  {p.name}
                                </Badge>
                              ) : null;
                            })()}
                            {(it.equivalence_group_id || catalogById[it.catalog_item_id]?.equivalence_group_id) && (() => {
                              const gid = it.equivalence_group_id || catalogById[it.catalog_item_id]?.equivalence_group_id;
                              const g = groupById[gid];
                              return g ? (
                                <Badge variant="secondary" className="text-[10px] bg-amber-50 text-amber-800">
                                  <Layers className="h-2.5 w-2.5 mr-0.5" />
                                  {g.name}
                                </Badge>
                              ) : null;
                            })()}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span>Quantité : {it.quantity || 1}</span>
                            {it.delivery_due_date && (
                              <span className="inline-flex items-center gap-1">
                                <Truck className="h-3 w-3" />
                                À respecter : {it.delivery_due_date}
                              </span>
                            )}
                            {it.delivery_place && (
                              <span className="inline-flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {it.delivery_place === "other"
                                  ? (it.delivery_place_custom || "Autre")
                                  : DELIVERY_PLACE_LABEL[it.delivery_place]}
                              </span>
                            )}
                            {it.worker_pickup_required && (
                              <Badge variant="secondary" className="text-[10px] bg-orange-100 text-orange-800">À récupérer</Badge>
                            )}
                          </div>
                        </div>
                        <Select
                          value={it.status || "todo"}
                          onValueChange={(v) => handleStatusChange(it, v)}
                        >
                          <SelectTrigger
                            className={`h-8 px-2 text-xs w-[160px] ${STATUS_COLOR[it.status] || ""}`}
                            data-testid={`logi-status-${it.id}`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {STATUS_OPTIONS.map((s) => (
                              <SelectItem key={s.value} value={s.value} data-testid={`logi-status-opt-${s.value}-${it.id}`}>
                                {s.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <div
                          className="flex items-center gap-1.5 text-xs"
                          title="Afficher dans le descriptif et la fiche de chantier"
                        >
                          <Switch
                            checked={!!it.show_in_worksheet}
                            onCheckedChange={() => handleToggleShow(it)}
                            data-testid={`logi-show-${it.id}`}
                          />
                          {it.show_in_worksheet
                            ? <Eye className="h-3.5 w-3.5 text-blue-700" />
                            : <EyeOff className="h-3.5 w-3.5 text-slate-400" />}
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(it)}
                          data-testid={`logi-delete-${it.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                      {expandedId === it.id && (
                        <LogisticsItemDetails
                          item={it}
                          orderId={order.id}
                          onSaved={reload}
                        />
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export { STATUS_OPTIONS, STATUS_LABEL };
